import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    OnInit,
    Output
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Observable } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { ToastService } from "~app/common/components/toast/toast.service";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ButtonComponent } from "../../../../button/button.component";
import { CheckboxComponent } from "../../../../checkbox/checkbox.component";
import { TogglePanelComponent } from "../../../../toggle-panel/toggle-panel.component";
import { IMapComponent } from "../../../map.models";
import {
    IMapDrawLayer,
    IMapDrawOption,
    IMapGeometry,
    IMapMediaLayerContainer,
    IMapSettings,
    IWidgetMenuButton
} from "../../../map.settings";
import { MapEditService } from "../../../services/map-edit.service";
import { MapPopupService } from "../../widgets/map-popup/map-popup.service";
import { MapBaseSubComponent } from "../map-base-sub-component/map-base-sub.component";
import { IDrawGroup, IDrawItem, MapDrawService } from "./map-draw.service";

@Component({
    selector: "rask-map-draw",
    templateUrl: "./map-draw.component.html",
    styleUrls: ["./map-draw.component.scss"],
    providers: [
        {
            provide: MapBaseSubComponent,
            useExisting: forwardRef(() => MapDrawComponent)
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent,
        ButtonComponent,
        CheckboxComponent,
        FormsModule
    ]
})
export class MapDrawComponent extends MapBaseSubComponent implements OnInit {
    readonly mapDrawService = inject(MapDrawService);
    readonly authService = inject(AuthenticationService);
    readonly mapPopupService = inject(MapPopupService);
    readonly mapEditService = inject(MapEditService);
    readonly toastService = inject(ToastService);
    readonly groupExpandedState = new Map<any, boolean>();

    widgetId = "map-draw";
    map: IMapComponent;
    drawWidget: any;
    groupFromLayers = true;
    drawGroups: IDrawGroup[] = [];
    mediaLayers: IMapMediaLayerContainer[] = [];
    hidden = false;
    isEnabled = true;
    isDrawing: boolean;
    @Input()
    set enabled(value: boolean) {
        this.isEnabled = value;
    }
    get enabled() {
        return this.isEnabled;
    }
    @Input()
    set groups(groups: IDrawGroup[]) {
        if (groups) {
            this.groupFromLayers = false;
            this.mapDrawService.updateDrawGroups(this.drawGroups, groups);
            this.clearDuplicates();
        } else {
            this.groupFromLayers = true;
        }
    }
    get groups() {
        return this.drawGroups;
    }
    @Output() active = new EventEmitter();

    buttons = [
        {
            iconClass: "fa-pencil",
            tooltipText: "Rita",
            visible: () => this.enabled && this.authService.canEdit() && !this.hideButton,
            widgetId: this.widgetId
        } as IWidgetMenuButton
    ];

    private layers: IMapDrawLayer[];

    constructor() {
        super();
        this.setupEvents();
    }

    setupEvents() {
        this.mapReady.pipe(
            takeUntil(this.destroy$)
        ).subscribe(m => {
            this.map = m;
            this.map.getSketch().onStateChange.subscribe((state) => {
                this.setIsDrawing(state);
            });
        });
        this.onLayerRefresh.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => this.update());
        this.mapPopupService.popupOpened.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.hidden = true;
        });
        this.mapPopupService.popupClosed.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.hidden = false;
        });
    }

    ngOnInit(): void {
        this.onHide.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.cancel();
        });
    }

    preInit(mapSettings: IMapSettings) {
        this.layers = mapSettings.drawLayers;
        if (this.groupFromLayers) {
            this.drawGroups = this.mapDrawService.getDefaultDrawGroupsFromLayers(this.layers);
            this.clearDuplicates();
        }
        this.mediaLayers = mapSettings.mediaLayers?.filter(ml => ml.visible) ?? [];
    }

    update() {
        if (this.groupFromLayers) {
            this.mapDrawService.updateDrawGroups(this.drawGroups, this.mapDrawService.getDefaultDrawGroupsFromLayers(this.layers));
            this.clearDuplicates();
        }
    }

    mouseover(layer: IMapDrawLayer, geometry: IMapGeometry, mouseEvent) {
        this.map.highlightGeometries(layer.id, [geometry]);
        return layer.onHoverIn && layer.onHoverIn(geometry, mouseEvent);
    }

    mouseout(layer: IMapDrawLayer, geometry: IMapGeometry, mouseEvent) {
        this.map?.clearAllHighlights();
        return layer.onHoverOut && layer.onHoverOut(geometry, mouseEvent);
    }

    click(layer: IMapDrawLayer, geometry: IMapGeometry, mouseEvent) {
        this.map.gotoGeometry(geometry, layer.id);
        return layer.onClick && layer.onClick(null, geometry);
    }

    optionClick(layer: IMapDrawLayer, option: IMapDrawOption) {
        if (ObjectUtils.isNullOrUndefined(option.onBeforeDraw) || option.onBeforeDraw() === true) {
            if (option.hideMenuOnDraw) {
                this.hidden = true;
            }

            if (option.manuallyHandledCallback) {
                option.callback(null, null);
                return undefined;
            }

            return this.map.getSketch().sketch(layer, option.type, (g) => {
                this.setIsDrawing(false);
                this.handleCallbackResult(option.callback(g, option.param));
            });
        }
        return undefined;
    }

    paste(layer: IMapDrawLayer, option: IMapDrawOption) {
        const clipboardItems = this.mapEditService.pasteFromInternalType(option.type);
        if (clipboardItems?.length > 0) {
            const singleItem = clipboardItems[0];
            this.map.addGeometries(layer.id, [singleItem]);
            this.handleCallbackResult(option.callback(singleItem, option.param));
        } else {
            this.toastService.add(`Det fanns ingen geometri av typ '${option.type}' i urklippet`, 2, 2000);
        }
    }

    cancel() {
        this.map?.getSketch().cancelSketch(null);
        this.setIsDrawing(false);
        this.hidden = false;
    }

    displayCounter = (group: IDrawGroup, index: number) => !group.hideCounter ? `${index + 1} -` : "";

    showDefaultOpen = (group: IDrawGroup): boolean => group?.items?.length > 0;

    isGroupExpanded = (group: IDrawGroup): boolean => {
        if (this.groupExpandedState.has(group.id)) {
            return this.groupExpandedState.get(group.id);
        }
        return this.showDefaultOpen(group) || (group.drawOptions?.length > 1);
    };

    onGroupToggle(group: IDrawGroup, expanded: boolean): void {
        this.groupExpandedState.set(group.id, expanded);
    }

    private handleCallbackResult(observable: Observable<any> | void) {
        if (ObjectUtils.isNullOrUndefined(observable)) {
            this.hidden = false;
        } else {
            (observable as Observable<any>).pipe(
                take(1)
            ).subscribe(() => {
                this.hidden = false;
            });
        }
    }

    private setIsDrawing(state) {
        this.isDrawing = state === "active";
        this.active.emit(this.isDrawing);
    }

    private clearDuplicates() {
        this.drawGroups.forEach(p => {
            const res: IDrawItem[] = [];

            p.items.forEach(pp => {
                if (res.findIndex(x => x.geometry?.id === pp.geometry?.id) === -1) {
                    res.push(pp);
                }
            });

            p.items = res;
        });
    }
}
